import React, { useContext, useEffect, useState } from "react";
import {
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Drawer,
} from "@mui/material";
import { Edit, Delete, Star, StarOutline, Share, Add } from "@mui/icons-material"; // Icons for preferred garden
import GardenContext from "../context/GardenContext";
import AuthContext from "../auth/AuthContext";
import apiClient from "../utils/apiClient";
import { useNavigate } from "react-router-dom"; // Import Link
import GardenForm from "../components/GardenForm";
import GardenShareModal from "../components/GardenShareModal";

const Gardens = () => {
  const {
    gardens,
    setGardens,
    setSelectedGarden,
    isSidebarOpen,
    setSidebarOpen,
    deleteGarden,
  } = useContext(GardenContext);
  const { auth, user, setUser } = useContext(AuthContext);
  const navigate = useNavigate();
  
  // State for garden sharing
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [selectedGardenForSharing, setSelectedGardenForSharing] = useState(null);
  const [gardenUserRoles, setGardenUserRoles] = useState({}); // {garden_uuid: role}
  
  // State for garden form modal
  const [gardenFormModalOpen, setGardenFormModalOpen] = useState(false);
  const [selectedGardenForEdit, setSelectedGardenForEdit] = useState(null);

  useEffect(() => {
    const fetchGardens = async () => {
      try {
        const response = await apiClient.get("/gardens");
        setGardens(response.data);
        
        // Fetch user roles for each garden
        const roles = {};
        for (const garden of response.data) {
          try {
            const roleResponse = await apiClient.get(`/gardens/${garden.garden_uuid}/users`);
            roles[garden.garden_uuid] = roleResponse.data.current_user_role;
          } catch (err) {
            console.error(`Failed to fetch role for garden ${garden.garden_uuid}:`, err);
            roles[garden.garden_uuid] = 'viewer'; // Default fallback
          }
        }
        setGardenUserRoles(roles);
      } catch (err) {
        console.error("Failed to fetch gardens:", err);
      }
    };

    if (auth?.token) {
      fetchGardens();
    }
  }, [auth, setGardens]);


  const handleEdit = (event, garden) => {
    event.stopPropagation();
    setSelectedGardenForEdit(garden);
    setSelectedGarden(garden);
    setGardenFormModalOpen(true);
  };
  
  const handleAddNewGarden = () => {
    setSelectedGardenForEdit(null);
    setSelectedGarden(null);
    setGardenFormModalOpen(true);
  };
  
  const handleGardenFormClose = () => {
    setGardenFormModalOpen(false);
    setSelectedGardenForEdit(null);
    setSelectedGarden(null);
  };

  const handleDelete = async (event, gardenUuid) => {
    event.stopPropagation();
    try {
      await apiClient.delete(`/gardens/${gardenUuid}`);
      deleteGarden(gardenUuid);
    } catch (err) {
      console.error("Failed to delete garden:", err);
    }
  };

  const handleGardenClick = (gardenUuid) => {
    navigate(`/gardens/${gardenUuid}`);
  };

  const handleShare = (event, garden) => {
    event.stopPropagation();
    setSelectedGardenForSharing(garden);
    setShareModalOpen(true);
  };

  const handleSetPreferredGarden = async (event, gardenUuid) => {
    event.stopPropagation();
    try {
      await apiClient.put(`/users/preferred-garden/${gardenUuid}`);
      // Refetch user data from API to ensure consistency with sidebar
      const userResponse = await apiClient.get("/me");
      setUser(userResponse.data);
    } catch (err) {
      console.error("Failed to set preferred garden:", err);
    }
  };

  return (
    <Box>
      <Typography 
        variant="h3" 
        gutterBottom
        className="vintage-script-header"
      >
        Gardens
      </Typography>
      <Button
        variant="outlined"
        onClick={handleAddNewGarden}
        className="vintage-button add-garden-button"
        sx={{ mb: 2 }}
      >
        ✚ Add New Garden
      </Button>
      <TableContainer component={Paper} sx={{ marginTop: 2 }} className="gardens-table">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell className="column-name">Name</TableCell>
              <TableCell className="column-width">Width</TableCell>
              <TableCell className="column-depth">Depth</TableCell>
              <TableCell className="column-unit">Unit</TableCell>
              <TableCell className="column-preferred">Preferred</TableCell>
              <TableCell className="column-actions"></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {gardens.map((garden) => (
              <TableRow key={garden.garden_uuid}
              hover
              onClick={() => handleGardenClick(garden.garden_uuid)}
              sx={{ cursor: "pointer" }} // Style for clickable row
                  >
                <TableCell className="column-name">{garden.name}</TableCell>
                <TableCell className="column-width">{garden.width}</TableCell>
                <TableCell className="column-depth">{garden.depth}</TableCell>
                <TableCell className="column-unit">{garden.unit}</TableCell>
                <TableCell className="column-preferred">
                  {user?.preferred_garden?.garden_uuid === garden.garden_uuid ? (
                    <Star color="primary" />
                  ) : (
                    <IconButton onClick={(event) => handleSetPreferredGarden(event, garden.garden_uuid)}>
                      <StarOutline />
                    </IconButton>
                  )}
                </TableCell>
                <TableCell 
                  className="column-actions"
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    textAlign: 'right',
                  }}
                >
                  <Box 
                    className="garden-actions-box"
                    sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.5 }}
                  >
                    <IconButton 
                      className="edit-button"
                      onClick={(event) => handleEdit(event, garden)}
                    >
                      <Edit />
                    </IconButton>
                    {gardenUserRoles[garden.garden_uuid] === 'owner' && (
                      <IconButton 
                        className="share-button"
                        onClick={(event) => handleShare(event, garden)} 
                        title="Share Garden"
                      >
                        <Share />
                      </IconButton>
                    )}
                    <IconButton 
                      className="delete-button"
                      onClick={(event) => handleDelete(event, garden.garden_uuid)}
                    >
                      <Delete />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            
            {/* Add New Garden Pseudo Row - at the bottom */}
            <TableRow 
              className="add-garden-row"
              onClick={handleAddNewGarden}
              sx={{ 
                cursor: "pointer",
                '&:hover': {
                  backgroundColor: 'rgba(201, 169, 97, 0.1) !important'
                }
              }}
            >
              <TableCell className="column-name">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Add sx={{ fontSize: '1.2rem', color: 'var(--color-sepia)' }} />
                  <Typography sx={{ fontFamily: 'var(--font-serif)', color: 'var(--color-sepia)', fontStyle: 'italic' }}>
                    Add New Garden
                  </Typography>
                </Box>
              </TableCell>
              <TableCell className="column-width"></TableCell>
              <TableCell className="column-depth"></TableCell>
              <TableCell className="column-unit"></TableCell>
              <TableCell className="column-preferred"></TableCell>
              <TableCell className="column-actions"></TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      <GardenForm
        open={gardenFormModalOpen}
        onClose={handleGardenFormClose}
        garden={selectedGardenForEdit}
      />
      
      <GardenShareModal
        open={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        garden={selectedGardenForSharing}
      />
    </Box>
  );
};

export default Gardens;
